import { useState } from "react";
import { AppLayout } from "@project/components/AppLayout";
import {
  mockCandidates,
  type Candidate,
  type CandidateStage,
} from "@project/data/mockData";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import { Input } from "@project/components/ui/input";
import { Button } from "@project/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@project/components/ui/tabs";
import { Textarea } from "@project/components/ui/textarea";
import {
  Search,
  Download,
  Eye,
  Star,
  Users,
  UserCheck,
  Send,
  FileText,
  Sparkles,
  Loader2,
  Upload,
  ClipboardPaste,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  MapPin,
  Briefcase,
  GraduationCap,
  Phone,
  Mail,
  MessageSquareText,
  GitCompare,
  Brain,
} from "lucide-react";
import { motion } from "framer-motion";
import {
  staggerContainer as container,
  fadeInUp as item,
} from "@project/lib/animations";
import { toast } from "@project/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useAIAction } from "@project/hooks/useAIAction";
import AIResultDisplay from "@project/components/AIResultDisplay";

const stageOptions: CandidateStage[] = [
  "Applied",
  "Screened",
  "Shortlisted",
  "Interview",
  "Offered",
  "Hired",
  "Rejected",
];
type StageTab = "All" | CandidateStage;

const stageColors: Record<string, string> = {
  Applied: "text-[hsl(var(--status-posted))] bg-[hsl(var(--status-posted-bg))]",
  Screened: "text-[hsl(var(--info))] bg-[hsl(200,80%,96%)]",
  Shortlisted:
    "text-[hsl(var(--status-onhold))] bg-[hsl(var(--status-onhold-bg))]",
  Interview: "text-[hsl(170,60%,42%)] bg-[hsl(170,60%,94%)]",
  Offered: "text-[hsl(var(--status-active))] bg-[hsl(var(--status-active-bg))]",
  Hired: "text-[hsl(130,60%,40%)] bg-[hsl(130,60%,94%)]",
  Rejected: "text-destructive bg-[hsl(var(--status-filled-bg))]",
};

const scoreColor = (score: number) => {
  if (score >= 90) {
    return "text-[hsl(var(--status-active))] bg-[hsl(var(--status-active-bg))]";
  }
  if (score >= 75) {
    return "text-[hsl(var(--status-posted))] bg-[hsl(var(--status-posted-bg))]";
  }
  if (score >= 60) {
    return "text-[hsl(var(--status-onhold))] bg-[hsl(var(--status-onhold-bg))]";
  }
  return "text-destructive bg-[hsl(var(--status-filled-bg))]";
};

const avatarColors = [
  "from-primary to-primary-glow",
  "from-[hsl(160_84%_39%)] to-[hsl(160_70%_50%)]",
  "from-[hsl(38_92%_50%)] to-[hsl(38_80%_60%)]",
  "from-[hsl(199_89%_48%)] to-[hsl(199_70%_58%)]",
  "from-[hsl(350_80%_55%)] to-[hsl(350_70%_60%)]",
];

const CandidatesPage = () => {
  const [activeStage, setActiveStage] = useState<StageTab>("All");
  const [search, setSearch] = useState("");
  const [candidates, setCandidates] = useState(mockCandidates);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(
    null
  );
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [resumeText, setResumeText] = useState("");
  const [isParsing, setIsParsing] = useState(false);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [aiInputMode, setAiInputMode] = useState<"upload" | "paste">("upload");
  const [aiEmailOpen, setAiEmailOpen] = useState(false);
  const [aiCompareOpen, setAiCompareOpen] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState<string[]>([]);
  const aiEmail = useAIAction();
  const aiCompare = useAIAction();
  const navigate = useNavigate();

  const stageTabs: { label: StageTab; count: number; icon: any }[] = [
    { label: "All", count: candidates.length, icon: Users },
    {
      label: "Applied",
      count: candidates.filter(c => c.stage === "Applied").length,
      icon: FileText,
    },
    {
      label: "Screened",
      count: candidates.filter(c => c.stage === "Screened").length,
      icon: Eye,
    },
    {
      label: "Shortlisted",
      count: candidates.filter(c => c.stage === "Shortlisted").length,
      icon: Star,
    },
    {
      label: "Interview",
      count: candidates.filter(c => c.stage === "Interview").length,
      icon: UserCheck,
    },
    {
      label: "Offered",
      count: candidates.filter(c => c.stage === "Offered").length,
      icon: Send,
    },
    {
      label: "Hired",
      count: candidates.filter(c => c.stage === "Hired").length,
      icon: CheckCircle2,
    },
  ];

  const filtered = (
    activeStage === "All"
      ? candidates
      : candidates.filter(c => c.stage === activeStage)
  ).filter(
    c =>
      !search ||
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.jobTitle.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase())
  );

  const handleStageChange = (id: string, newStage: string) => {
    setCandidates(prev =>
      prev.map(c =>
        c.id === id ? { ...c, stage: newStage as CandidateStage } : c
      )
    );
    toast({ title: "Stage Updated" });
  };

  const handleExport = () => {
    const csv = [
      "Name,Email,Job,Match Score,Stage",
      ...candidates.map(
        c => `${c.name},${c.email},${c.jobTitle},${c.matchScore}%,${c.stage}`
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "candidates.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Exported" });
  };

  const handleParseResume = async () => {
    toast({
      title: "Feature Disabled",
      description:
        "AI Resume parsing is currently unavailable because Supabase has been removed.",
      variant: "destructive",
    });
  };

  return (
    <AppLayout>
      <motion.div
        className="space-y-4"
        variants={container}
        initial="hidden"
        animate="show"
      >
        {/* Header */}
        <motion.div
          variants={item}
          className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center"
        >
          <div>
            <h1 className="font-display text-lg font-bold tracking-tight text-foreground">
              Candidates
            </h1>
            <p className="mt-0.5 text-[12px] text-muted-foreground">
              {candidates.length} total candidates in pipeline
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="ai-btn h-8 gap-1.5 rounded-lg text-[12px] font-semibold"
              onClick={async () => {
                const top = candidates.slice(0, 5);
                setAiCompareOpen(true);
                await aiCompare.run(`Compare these candidates and rank them. Provide a detailed comparison table and recommendation:

${top.map(c => `- **${c.name}**: Role=${c.jobTitle}, Match=${c.matchScore}%, Exp=${c.experience}, Skills=${c.skills.join(", ")}, Red Flags=${c.redFlags.length > 0 ? c.redFlags.join("; ") : "None"}`).join("\n")}

Provide:
1. **Ranking** (best to least suitable)
2. **Comparison Matrix** (skills, experience, match score)
3. **Strengths & Weaknesses** for each
4. **Final Recommendation** with reasoning`);
              }}
            >
              <GitCompare className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">AI Compare</span>
            </Button>
            <Button
              size="sm"
              className="ai-btn h-8 gap-1.5 rounded-lg text-[12px] font-semibold"
              onClick={() => setAiDialogOpen(true)}
            >
              <Sparkles className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Add from Resume</span>
              <span className="sm:hidden">AI Resume</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 rounded-lg text-[12px]"
              onClick={handleExport}
            >
              <Download className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Export</span>
            </Button>
          </div>
        </motion.div>

        {/* Stage Filters */}
        <motion.div
          variants={item}
          className="no-scrollbar flex items-center gap-0.5 overflow-x-auto rounded-xl bg-muted/50 p-0.5"
        >
          {stageTabs.map(tab => (
            <button
              key={tab.label}
              onClick={() => setActiveStage(tab.label)}
              className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-[12px] font-medium transition-all ${activeStage === tab.label ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            >
              {tab.label}
              <span
                className={`rounded-md px-1.5 py-0 text-[10px] font-bold ${activeStage === tab.label ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </motion.div>

        {/* Search */}
        <motion.div variants={item}>
          <div className="relative max-w-xs">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search candidates..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="h-8 rounded-lg border-border/60 bg-card pl-8 text-[12px]"
            />
          </div>
        </motion.div>

        {/* Table */}
        <motion.div variants={item}>
          {filtered.length === 0 ? (
            <div className="section-card p-8 text-center">
              <Users className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
              <p className="text-[13px] text-muted-foreground">
                No candidates found
              </p>
            </div>
          ) : (
            <>
              {/* Mobile Cards */}
              <div className="space-y-2 sm:hidden">
                {filtered.map((c, i) => (
                  <div
                    key={c.id}
                    className="section-card space-y-2.5 p-3"
                    onClick={() => setSelectedCandidate(c)}
                  >
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`h-9 w-9 rounded-xl bg-gradient-to-br ${avatarColors[i % avatarColors.length]} flex shrink-0 items-center justify-center`}
                      >
                        <span className="text-[10px] font-bold text-white">
                          {c.avatar}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-semibold text-foreground">
                          {c.name}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {c.jobTitle}
                        </p>
                      </div>
                      <span
                        className={`rounded-lg px-2 py-1 text-[10px] font-bold ${scoreColor(c.matchScore)}`}
                      >
                        {c.matchScore}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {c.location}
                      </span>
                      <span>{c.experience} @ {c.previousCompany}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span
                        className={`rounded-lg px-2 py-0.5 text-[10px] font-semibold ${stageColors[c.stage]}`}
                      >
                        {c.stage}
                      </span>
                      {c.redFlags.length > 0 ? (
                        <span className="flex items-center gap-1 text-[10px] text-destructive">
                          <AlertTriangle className="h-3 w-3" />
                          {c.redFlags.length} flags
                        </span>
                      ) : (
                        <span className="text-[10px] text-[hsl(var(--status-active))]">
                          ✓ Clear
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table */}
              <div className="section-card hidden overflow-hidden sm:block">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30 hover:bg-muted/30">
                        <TableHead className="pl-5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                          Candidate
                        </TableHead>
                        <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                          Phone
                        </TableHead>
                        <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                          Applied For
                        </TableHead>
                        <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                          Match Score
                        </TableHead>
                        <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                          Experience
                        </TableHead>
                        <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                          Location
                        </TableHead>
                        <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                          Stage
                        </TableHead>
                        <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                          Red Flags
                        </TableHead>
                        <TableHead className="w-[80px] text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                          Action
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((c, i) => (
                        <TableRow key={c.id} className="table-row-hover">
                          <TableCell className="pl-5">
                            <div className="flex items-center gap-2.5">
                              <div
                                className={`h-8 w-8 rounded-xl bg-gradient-to-br ${avatarColors[i % avatarColors.length]} flex shrink-0 items-center justify-center`}
                              >
                                <span className="text-[9px] font-bold text-white">
                                  {c.avatar}
                                </span>
                              </div>
                              <div>
                                <p className="text-[12px] font-semibold text-foreground">
                                  {c.name}
                                </p>
                                <p className="text-[10px] text-muted-foreground">
                                  {c.email}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-[12px] text-muted-foreground">
                            <div className="flex items-center gap-1.5">
                              <Phone className="h-3.5 w-3.5" />
                              {c.phone}
                            </div>
                          </TableCell>
                          <TableCell className="text-[12px] text-foreground">
                            {c.jobTitle}
                          </TableCell>
                          <TableCell>
                            <span
                              className={`rounded-lg px-2 py-1 text-[11px] font-bold ${scoreColor(c.matchScore)}`}
                            >
                              <TrendingUp className="mr-1 inline h-3 w-3" />
                              {c.matchScore}%
                            </span>
                          </TableCell>
                          <TableCell className="text-[12px] text-muted-foreground">
                            <div>
                              <p className="text-[12px] font-medium text-foreground">
                                {c.experience}
                              </p>
                              <p className="text-[10px] text-muted-foreground">
                                @ {c.previousCompany}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="text-[12px] text-muted-foreground">
                            <div className="flex items-center gap-1.5">
                              <MapPin className="h-3.5 w-3.5" />
                              {c.location}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={c.stage}
                              onValueChange={v => handleStageChange(c.id, v)}
                            >
                              <SelectTrigger
                                className={`h-7 w-[120px] rounded-lg border-0 text-[11px] ${stageColors[c.stage]}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {stageOptions.map(s => (
                                  <SelectItem
                                    key={s}
                                    value={s}
                                    className="text-[12px]"
                                  >
                                    {s}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            {c.redFlags.length > 0 ? (
                              <span className="flex items-center gap-1 text-[10px] text-destructive">
                                <AlertTriangle className="h-3 w-3" />
                                {c.redFlags.length}
                              </span>
                            ) : (
                              <span className="text-[10px] text-[hsl(var(--status-active))]">
                                ✓ Clear
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <button
                              onClick={() => setSelectedCandidate(c)}
                              className="rounded-lg p-1.5 transition-colors hover:bg-muted"
                            >
                              <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                            </button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>

      {/* Candidate Detail Dialog */}
      <Dialog
        open={!!selectedCandidate}
        onOpenChange={() => setSelectedCandidate(null)}
      >
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          {selectedCandidate && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3 font-display">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-glow">
                    <span className="text-[12px] font-bold text-white">
                      {selectedCandidate.avatar}
                    </span>
                  </div>
                  <div>
                    <p>{selectedCandidate.name}</p>
                    <p className="text-[12px] font-normal text-muted-foreground">
                      {selectedCandidate.jobTitle}
                    </p>
                  </div>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                {/* Match Score */}
                <div className="flex items-center gap-3 rounded-xl bg-muted/30 p-3">
                  <div
                    className={`font-display text-2xl font-bold ${selectedCandidate.matchScore >= 80 ? "text-[hsl(var(--status-active))]" : selectedCandidate.matchScore >= 60 ? "text-[hsl(var(--status-onhold))]" : "text-destructive"}`}
                  >
                    {selectedCandidate.matchScore}%
                  </div>
                  <div>
                    <p className="text-[12px] font-semibold text-foreground">
                      AI Match Score
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {selectedCandidate.aiSummary}
                    </p>
                  </div>
                </div>

                {/* Red Flags */}
                {selectedCandidate.redFlags.length > 0 && (
                  <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-3">
                    <p className="mb-1 flex items-center gap-1 text-[12px] font-semibold text-destructive">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      Red Flags
                    </p>
                    {selectedCandidate.redFlags.map((f, i) => (
                      <p key={i} className="text-[11px] text-destructive/80">
                        • {f}
                      </p>
                    ))}
                  </div>
                )}

                {/* Details */}
                <div className="grid grid-cols-2 gap-3 text-[12px]">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="h-3.5 w-3.5" />
                    {selectedCandidate.email}
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="h-3.5 w-3.5" />
                    {selectedCandidate.phone}
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5" />
                    {selectedCandidate.location}
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Briefcase className="h-3.5 w-3.5" />
                    {selectedCandidate.experience}
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <GraduationCap className="h-3.5 w-3.5" />
                    {selectedCandidate.education}
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Briefcase className="h-3.5 w-3.5" />
                    {selectedCandidate.previousCompany}
                  </div>
                </div>

                {/* Skills */}
                <div>
                  <p className="mb-1.5 text-[11px] font-semibold text-muted-foreground">
                    Skills
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedCandidate.skills.map(s => (
                      <span
                        key={s}
                        className="rounded-lg bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>

                {/* AI Actions */}
                <div className="flex gap-2 border-t border-border/30 pt-2">
                  <Button
                    size="sm"
                    className="ai-btn-outline flex-1 gap-1.5 rounded-lg text-[11px]"
                    onClick={async () => {
                      setAiEmailOpen(true);
                      await aiEmail.run(`Generate a professional, empathetic rejection email for candidate "${selectedCandidate.name}" who applied for "${selectedCandidate.jobTitle}". Include:
- Thank them for their application and time
- Briefly mention why they weren't selected (generic, respectful)
- Encourage future applications
- Professional sign-off
Keep it warm and professional.`);
                    }}
                  >
                    <MessageSquareText className="h-3.5 w-3.5" />
                    AI Rejection Email
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 gap-1.5 text-[11px]"
                    onClick={() =>
                      navigate(
                        `/interviews/schedule?candidateId=${selectedCandidate.id}`
                      )
                    }
                  >
                    Schedule Interview
                  </Button>
                </div>

                {/* Stage Change */}
                <div className="flex items-center gap-3">
                  <Select
                    value={selectedCandidate.stage}
                    onValueChange={v => {
                      handleStageChange(selectedCandidate.id, v);
                      setSelectedCandidate(prev =>
                        prev ? { ...prev, stage: v as CandidateStage } : null
                      );
                    }}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {stageOptions.map(s => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Resume Dialog */}
      <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Sparkles className="h-4 w-4 text-primary" />
              Add from Resume (AI)
            </DialogTitle>
          </DialogHeader>
          <Tabs
            value={aiInputMode}
            onValueChange={v => setAiInputMode(v as any)}
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="upload" className="gap-1.5 text-[12px]">
                <Upload className="h-3.5 w-3.5" />
                Attach Resume
              </TabsTrigger>
              <TabsTrigger value="paste" className="gap-1.5 text-[12px]">
                <ClipboardPaste className="h-3.5 w-3.5" />
                Paste Text
              </TabsTrigger>
            </TabsList>
            <TabsContent value="upload" className="mt-4">
              <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-border/60 p-8 transition-all hover:border-primary/40 hover:bg-primary/5">
                <Upload className="mb-2 h-8 w-8 text-muted-foreground" />
                <span className="text-[13px] font-medium text-foreground">
                  {resumeFile ? resumeFile.name : "Click to upload resume"}
                </span>
                <span className="mt-1 text-[11px] text-muted-foreground">
                  JPG, PNG, PDF, DOCX — Max 5MB
                </span>
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                  onChange={e => setResumeFile(e.target.files?.[0] || null)}
                />
              </label>
            </TabsContent>
            <TabsContent value="paste" className="mt-4">
              <Textarea
                value={resumeText}
                onChange={e => setResumeText(e.target.value)}
                placeholder="Paste candidate resume text here..."
                rows={8}
              />
            </TabsContent>
          </Tabs>
          <Button
            onClick={handleParseResume}
            disabled={isParsing}
            className="btn-glow mt-2 w-full"
          >
            {isParsing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Parsing with AI...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Parse & Add Candidate
              </>
            )}
          </Button>
        </DialogContent>
      </Dialog>

      {/* AI Rejection Email Dialog */}
      <Dialog open={aiEmailOpen} onOpenChange={setAiEmailOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <MessageSquareText className="h-4 w-4 text-primary" />
              AI Rejection Email
            </DialogTitle>
          </DialogHeader>
          {aiEmail.loading ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-[13px] text-muted-foreground">
                Generating email...
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="whitespace-pre-wrap rounded-xl bg-muted/30 p-4 text-[13px] leading-relaxed text-foreground">
                {aiEmail.result}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    navigator.clipboard.writeText(aiEmail.result);
                    toast({ title: "Copied!" });
                  }}
                >
                  Copy
                </Button>
                <Button
                  className="btn-glow flex-1"
                  onClick={() => {
                    toast({ title: "Email sent!" });
                    setAiEmailOpen(false);
                  }}
                >
                  <Send className="mr-1.5 h-3.5 w-3.5" />
                  Send Email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Compare Dialog */}
      <Dialog open={aiCompareOpen} onOpenChange={setAiCompareOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <GitCompare className="h-5 w-5 text-primary" />
              AI Candidate Comparison
            </DialogTitle>
          </DialogHeader>
          {aiCompare.loading ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-[13px] text-muted-foreground">
                Comparing candidates...
              </p>
            </div>
          ) : (
            <AIResultDisplay content={aiCompare.result} />
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default CandidatesPage;
